import Vue from 'vue';
import { Howler, Howl } from 'howler';
import musicModule from '@/store/sound/music.store';
import fxModule from '@/store/sound/fx.store';
import { randomValue } from '@/lib/random';
import { randomizeVolume } from '@/lib/sound';
import { LOCAL } from "@/store/mutations";

const { TOGGLE_MUTE, CREATE_SOUND, REMOVE_SOUND, CONTROL_SOUND } = LOCAL;

export default {
	namespaced: true,
	modules: {
		music: musicModule,
		fx: fxModule,
	},
	state: {
		fileType: Howler.codecs("webm") ? "mp3" : "mp3",
		muted: true,
		list: {},
	},
	getters: {
		playable: (state) => (sound) => state.list[sound.id]?.howl instanceof Howl,
		getConfig: (state, getters, rootState) => (sound) => rootState.global.api.data.sounds[sound.id],
		getVolume: (state, getters) => (sound) => randomizeVolume(getters.getConfig(sound)),
		getSrc: (state, getters, rootState, rootGetters) => (sound) => {
			const file = getters.randomFile(sound);
			return rootGetters["global/assets/get"](file.asset_id).url;
		},
		randomFile: (state, getters) => (sound) => randomValue(
			getters.filesByType(sound, state.fileType)
		),
		filesByType: (state, getters) => (sound, type) => getters.getConfig(sound).files.filter(
			(file) => file.type === type
		),
	},
	actions: {
		configureApp: {
			root: true,
			handler({ state }) {
				Howler.mute(state.muted);
			},
		},
		create({ state, commit, getters }, sound) {
			const { id, events, ...options } = sound;
			commit(CREATE_SOUND, {
				src: getters.getSrc(sound),
				format: state.fileType,
				volume: getters.getVolume(sound),
				autoplay: false,
				id,
				events,
				...options,
			});
		},
		async play({ dispatch, getters, commit }, sound) {
			if (!getters.playable(sound)) {
				await dispatch("create", sound);
			}

			commit(CONTROL_SOUND, { action: "play", id: sound.id });
		},
		pause({ commit }, sound) {
			commit(CONTROL_SOUND, { action: "pause", id: sound.id });
		},
		stop({ commit }, sound) {
			commit(CONTROL_SOUND, { action: "stop", id: sound.id });
		},
		fade({ state, commit }, sound) {
			const { howl } = state.list[sound.id];

			commit(CONTROL_SOUND, {
				action: "fade",
				id: sound.id,
				args: [howl.volume(), sound.volume, sound.duration],
			});
		},
		unmute({ commit }) {
			commit(TOGGLE_MUTE, { muted: false });
		},
		mute({ commit }) {
			commit(TOGGLE_MUTE, { muted: true });
		},
		toggle({ commit, state }) {
			commit(TOGGLE_MUTE, { muted: !state.muted });
		},
	},

	mutations: {
		[CREATE_SOUND](state, sound) {
			const { id, events, ...settings } = sound;
			const howl = new Howl(settings);

			Object.entries(events).forEach(([event, action]) => howl.on(event, action));
			Vue.set(state.list, id, { id, src: settings.src, howl });
		},
		[REMOVE_SOUND](state, sound) {
			state.list[sound.id].howl.unload();
			Vue.delete(state.list, sound.id);
		},
		[CONTROL_SOUND](state, sound) {
			const { id, action, args } = sound;
			const { howl } = state.list[id];

			if (action in howl) {
				howl[action](...(args || []));
			} else {
				// eslint-disable-next-line no-console
				console.warn(`Sound id ${id} has no method ${action}`);
			}
		},
		[TOGGLE_MUTE](state, sound) {
			Howler.mute(sound.muted);
			state.muted = sound.muted;
		},
	},
};



// WEBPACK FOOTER //
// ./src/store/sound/index.js